import React, { JSX } from 'react';
import moment from 'moment';
import { CalendarV2 } from '~/src/dash/components/calendar/CalendarV2';
import { INewCalDates } from '~/src/dash/components/calendar/CalendarV2/common';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { Col, Cols, Text, spacing, usePublicaTranslation } from '~/src/dash/publicaUI';
import { auctionInsightsTimeRanges, ITimeRange, TimeRange } from '~/src/dash/utils/dateTimeRanges';
import { LoadingState, LoadingStateLoading } from '~/src/dash/utils/loading-states';
import { DateTimeRangeToggles } from '~/src/dash/views/dashboards/DashboardHome/common/DateTimeRangeToggles';
import { PillSwitcherV2 } from '~/src/dash/components/tabs/PillSwitcherV2';

interface IProps {
  calStartDate: moment.Moment;
  calEndDate: moment.Moment;
  isDisabled?: boolean;
  handleApplyDatesChange: ({ newStartDate, newEndDate }: INewCalDates) => void;
  loadState: LoadingState;
  campaignStartDate: moment.Moment;
  campaignEndDate: moment.Moment;
  realTimeRange: ITimeRange;
  handleToggleRealTimeRange: (rangeKey: TimeRange) => void;
  isRealtimeOnly: boolean;
  handleToggleRealtimeOnly: () => void;
}

export const CampaignAuctionInsightsFilters = ({
  handleApplyDatesChange,
  campaignStartDate,
  campaignEndDate,
  calStartDate,
  calEndDate,
  isDisabled = false,
  loadState,
  realTimeRange,
  handleToggleRealTimeRange,
  isRealtimeOnly,
  handleToggleRealtimeOnly,
}: IProps): JSX.Element => {
  const { t } = usePublicaTranslation();
  return (
    <Cols
      gap={spacing.large}
      style={{ justifyContent: 'flex-start', alignItems: 'center', height: '48px' }}
    >
      <Col>
        <PillSwitcherV2
          isDisabled={loadState === LoadingStateLoading}
          size='smallExtraLong'
          options={[
            {
              value: 'Custom Date',
              tKey: 'CustomDate',
              label: 'Custom Date',
              isSelected: !isRealtimeOnly,
              key: 'customDate',
            },
            {
              value: 'Real-Time (L24 hrs)',
              label: 'Real-Time (L24 hrs)',
              tKey: 'RealTimeToggleText',
              isSelected: isRealtimeOnly,
              key: 'realTime',
            },
          ]}
          onChange={() => {
            handleToggleRealtimeOnly();
          }}
          tooltipText={isRealtimeOnly ? t('RealTimeToggleTooltipText') : t('CustomDateTooltipText')}
        />
      </Col>
      {!isRealtimeOnly ? (
        <>
          <Col>
            <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
              <Col displayFlex>
                <IconV2 name='calendar' small />
              </Col>
              <Col>
                <Text small bold tKey='Period' />
              </Col>
            </Cols>
          </Col>
          <Col>
            <CalendarV2
              calStartDate={calStartDate}
              calEndDate={calEndDate}
              handleApplyDatesChange={handleApplyDatesChange}
              isDisabled={isDisabled || loadState === LoadingStateLoading}
              uniqueID='insightsCal'
              minDate={campaignStartDate}
              maxDate={campaignEndDate.isBefore(moment()) ? campaignEndDate : moment()}
              customInitialVisibleMonth={() => calEndDate.clone().subtract(1, 'month')}
            />
          </Col>
        </>
      ) : (
        <>
          <Col>
            <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
              <Col displayFlex>
                <IconV2 name='clock' small />
              </Col>
              <Col>
                <Text small bold tKey='TimeRange' />
              </Col>
            </Cols>
          </Col>
          <Col>
            <DateTimeRangeToggles
              isDisabled={loadState === LoadingStateLoading}
              ranges={auctionInsightsTimeRanges}
              activeKey={realTimeRange.key}
              onClick={(rangeKey: TimeRange) => {
                handleToggleRealTimeRange(rangeKey);
              }}
            />
          </Col>
        </>
      )}
    </Cols>
  );
};
